//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import Tippy from '@tippyjs/react';
import React from 'react';
import { Instance, Placement } from 'tippy.js';

import { CuPopoverTippy } from '@components/molecules/popover/CuPopoverTippy';
import {
  IPopoverDelay,
  popoverDelayToTippyDelay,
} from '@components/molecules/popover/IPopoverDelay';
import {
  IPopoverOffset,
  popoverOffsetToTippyOffset,
} from '@components/molecules/popover/IPopoverOffset';
import { asPopoverTarget } from '@components/molecules/popover/asPopoverTarget';

// Tippy expects a numerical zIndex value, while our theme value is a str
const DEFAULT_POPOVER_Z_INDEX = parseInt(theme.zindex.popover, 10);

/**
 * The set of Popper.js modifiers to force the Popover to stay in place regardless
 * of surrounding elements. Should only be used at design's direction.
 */
const STATIC_MODIFIERS = {
  modifiers: [
    { name: 'flip', enabled: false },
    { name: 'preventOverflow', enabled: false },
  ],
};

/**
 * Gets or creates the wrapping element for all Popovers with the `shouldAppendToBody`
 * flag. We wrap this in the unison-component class so the Popover gets our body styles.
 */
const getPopoverBodyContainer = (() => {
  let popoverBodyContainer: Element;
  return () => {
    if (!popoverBodyContainer) {
      popoverBodyContainer = document.createElement('div');
      popoverBodyContainer.className =
        'unison-component crl8-tippy-popover-boi';
      document.body.appendChild(popoverBodyContainer);
    }
    return popoverBodyContainer;
  };
})();

interface IOwnProps {
  /**
   * Target and content types are copied directly from Tippy
   */
  target: React.ReactElement;
  content?: React.ReactChild | React.ReactChild[];

  /*
   * The element that the Popover should be appended to in the DOM. Using a custom
   * append target (rather than the default of 'parent') can help with z-index and
   * clipping issues, but is expensive. Use this sparingly!
   */
  appendTarget?: 'body' | React.ComponentProps<typeof Tippy>['appendTo'];
  className?: string;
  /**
   * Allows access to force open/close methods on the Popover via
   * the use of the usePopover hook. See the hook for more info.
   */
  connectPopover?: (instance: Instance) => void;
  shouldShowArrow?: boolean;
  shouldOpenOnHover?: boolean;
  delay?: IPopoverDelay;
  isDisabled?: boolean;
  isStatic?: boolean;
  offset?: IPopoverOffset;
  /**
   * A callback for when the Popover is open and closed.
   */
  onTogglePopover?: (isOpen: boolean, event: Event) => void;
  placement?: Placement;
  /**
   * A custom z-index for the Popover contents. Defaults to the Unison
   * theme.zindex.popover (1060).
   */
  zIndex?: number;
  interactive?: boolean;
}

export type Props = IOwnProps;

/**
 * This molecule is a thin wrapper around the Tippy tooltip
 * that approximates the functionality of the Unison Popover
 * component.
 *
 * It renders the given target with a styled div containing
 * the given content that appears on either hover or click.
 */
const Popover: React.FunctionComponent<Props> = ({
  appendTarget = 'parent',
  className = '',
  connectPopover = () => undefined,
  content,
  delay = {},
  isDisabled = false,
  isStatic = false,
  offset = {},
  onTogglePopover = () => undefined,
  placement = 'right-start',
  shouldOpenOnHover = true,
  shouldShowArrow = true,
  interactive = true,
  target,
  zIndex = DEFAULT_POPOVER_Z_INDEX,
}) => {
  const appendTo = (() => {
    switch (appendTarget) {
      case 'body':
        return getPopoverBodyContainer();
      default:
        return appendTarget ?? 'parent';
    }
  })();

  return (
    <CuPopoverTippy
      animation={false}
      appendTo={appendTo}
      arrow={shouldShowArrow}
      className={className}
      content={content}
      delay={popoverDelayToTippyDelay(delay)}
      disabled={isDisabled}
      duration={0}
      interactive={interactive}
      offset={popoverOffsetToTippyOffset(offset)}
      onCreate={connectPopover}
      onTrigger={(_instance, event) => onTogglePopover(true, event)}
      onUntrigger={(_instance, event) => onTogglePopover(false, event)}
      onClickOutside={(_instance, event) => onTogglePopover(false, event)}
      placement={placement}
      trigger={shouldOpenOnHover ? 'mouseenter focus focusin' : 'click'}
      zIndex={zIndex}
      maxWidth='none'
      popperOptions={isStatic ? STATIC_MODIFIERS : undefined}
    >
      {asPopoverTarget(target)}
    </CuPopoverTippy>
  );
};

export default Popover;
